/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */
//standard libraries
import { AxiosRequestConfig } from "axios";
//context
import { useCSRValue, useSelectedStoreValue } from "../../context";
//custom libraries
import {
  ORDERS_CAN_WORK_ON_BEHALF,
  METHOD,
  LOCK_ORDER,
  UNLOCK_ORDER,
  ORDERS_GET_ORDERITEM_BY_ORDERID,
  CANCEL_ORDER,
  ORDERS_POST_COMMENT_BY_ORDERID,
  ORDERS_GET_COMMENT_BY_ORDERID_V2,
  ORDERS_GET_COMMENT_BY_ORDERID,
  RE_ORDER,
} from "../../constants/requests";
import { useRestService } from "./use-rest-service";
import { ParamsJson } from "../use-order-form";
import { LIMIT } from "../../constants/common";
import { PaginationI } from "../../interfaces/pagination.interface";

export const useOrderHandler = () => {
  const { csr } = useCSRValue();
  const { executeV2Request, executeRequest } = useRestService();
  const { selectedStore } = useSelectedStoreValue();

  const transformV1Response = (data: any, headers: any) => {
    if (typeof data === "string") {
      data = JSON.parse(data);
      data.isLocked = false;
      if (data.locked === "true" || data.locked === true) {
        if (data.orderEditor?.uniqueID !== String(csr.userId)) {
          data.isLocked = true;
        } else {
          data.checkLock = true;
        }
      }
    }
    return data;
  };
  const findOrders = (searchParams: ParamsJson, offset: number, limit: number) => {
    let newParams = {
      ...searchParams,
      storeId: selectedStore.storeId,
      startIndex: offset,
      maxResults: limit,
    };
    const requestOptions: AxiosRequestConfig = {
      method: METHOD.GET,
      url: ORDERS_CAN_WORK_ON_BEHALF,
      params: newParams,
      headers: {
        "Cache-Control": "no-cache",
      },
    };
    return executeRequest(requestOptions);
  };

  const lockOrderREST = (orderId: string, takeOver: boolean) => {
    let requestOptions: AxiosRequestConfig;
    requestOptions = {
      method: METHOD.POST,
      url: LOCK_ORDER.replace("{storeId}", selectedStore.storeId).replace("{cartId}", orderId),
    };
    if (takeOver) {
      requestOptions = {
        ...requestOptions,
        data: { takeOverLock: "Y" },
      };
    }
    return executeRequest(requestOptions);
  };

  const unlockOrderREST = (orderId: string) => {
    const requestOptions: AxiosRequestConfig = {
      method: METHOD.POST,
      url: UNLOCK_ORDER.replace("{storeId}", selectedStore.storeId).replace("{cartId}", orderId),
    };
    return executeRequest(requestOptions);
  };

  const cancelOrder = (id: string) => {
    const url = CANCEL_ORDER.replace("{id}", id);
    const requestOptions: AxiosRequestConfig = {
      method: METHOD.POST,
      url: url,
      params: {
        storeId: selectedStore.storeId,
      },
      data: {},
    };
    return executeV2Request(requestOptions);
  };

  const findOrderByID = (orderId: string, pageNumber1?: number) => {
    let pageNumber = pageNumber1 ? pageNumber1 : 1;
    let newParams = {
      pageNumber,
      pageSize: LIMIT,
    };

    const requestOptions: AxiosRequestConfig = {
      method: METHOD.GET,
      url: ORDERS_GET_ORDERITEM_BY_ORDERID.replace("{storeid}", selectedStore.storeId).replace("{orderid}", orderId),
      params: newParams,
      transformResponse: [transformV1Response],
    };

    return executeRequest(requestOptions);
  };

  const postOrderCommentByID = (orderId: string, comment: string, email = null) => {
    let newParams = {
      storeId: selectedStore.storeId,
    };

    const requestOptions: AxiosRequestConfig = {
      method: METHOD.POST,
      url: ORDERS_POST_COMMENT_BY_ORDERID.replace("{orderId}", orderId).replace("{storeId}", selectedStore.storeId),
      params: newParams,
      data: { sendEmail: email, commentField: comment },
    };
    return executeRequest(requestOptions);
  };

  const getOrderCommentByIDV2 = (orderId: string) => {
    const requestOptions: AxiosRequestConfig = {
      method: METHOD.GET,
      url: ORDERS_GET_COMMENT_BY_ORDERID_V2.replace("{orderid}", orderId),
    };

    return executeV2Request(requestOptions);
  };

  const getOrderCommentByID = (orderId: string) => {
    const requestOptions: AxiosRequestConfig = {
      method: METHOD.GET,
      url: ORDERS_GET_COMMENT_BY_ORDERID.replace("{storeid}", selectedStore.storeId).replace("{orderid}", orderId),
    };

    return executeRequest(requestOptions);
  };

  const reOrder = (params: any, skipMessageSnackbar = false) => {
    const url = RE_ORDER.replace("{storeId}", selectedStore.storeId);
    const requestOptions: AxiosRequestConfig | any = {
      method: METHOD.POST,
      url: url,
      params,
      skipMessageSnackbar,
    };
    return executeRequest(requestOptions);
  };

  const getOrdersPagination = ({
    page,
    size,
    ffmCenterId,
    orderNumber,
    shippingStatus,
    storeId,
    status,
  }: PaginationI) => {
    const requestOptions: AxiosRequestConfig | any = {
      method: METHOD.GET,
      url: `/orders?page=${page}&size=${size}&storeId=${storeId || ""}&ffmCenterId=${ffmCenterId || ""}&orderNo=${
        orderNumber || ""
      }&shippingStatus=${shippingStatus !== null && shippingStatus !== undefined ? shippingStatus : ""}&status=${
        status || ""
      }`,
    };

    return executeRequest(requestOptions);
  };

  const getOrdersById = (id: number) => {
    const requestOptions: AxiosRequestConfig | any = {
      method: METHOD.GET,
      url: `/orders/${id}`,
    };
    return executeRequest(requestOptions);
  };

  const getOrdersPaginationBySeller = ({ page, size, orderNumber, shippingStatus, status }: PaginationI) => {
    const requestOptions: AxiosRequestConfig | any = {
      method: METHOD.GET,
      url: `/orders/by-seller?page=${page}&size=${size}&orderNo=${orderNumber || ""}&shippingStatus=${
        shippingStatus !== null && shippingStatus !== undefined ? shippingStatus : ""
      }&status=${status || ""}`,
    };

    return executeRequest(requestOptions);
  };

  const processOrderItem = ({ orderId, orderItemId }: { orderId: number; orderItemId: number }) => {
    const requestOptions: AxiosRequestConfig | any = {
      method: METHOD.POST,
      url: `/orders/${orderId}/order-items/${orderItemId}/place`,
    };

    return executeRequest(requestOptions);
  };

  const updateOrderItemFfmCenter = ({
    orderId,
    orderItemId,
    storeId,
    ffmCenterIdentifier,
  }: {
    orderId: number;
    orderItemId: number;
    storeId: string;
    ffmCenterIdentifier: string;
  }) => {
    const requestOptions: AxiosRequestConfig | any = {
      method: METHOD.PATCH,
      url: `/orders/${orderId}/order-items/${orderItemId}`,
      data: {
        storeId: storeId.toString(),
        ffmCenterIdentifier: ffmCenterIdentifier,
      },
    };

    return executeRequest(requestOptions);
  };
  const cancelOrderItem = ({
    orderId,
    orderItemId,
    storeId,
  }: {
    orderId: number;
    orderItemId: number;
    storeId: string;
  }) => {
    const requestOptions: AxiosRequestConfig | any = {
      method: METHOD.PATCH,
      url: `/orders/${orderId}/order-items/${orderItemId}/cancel`,
      data: {
        storeId: storeId.toString(),
      },
    };

    return executeRequest(requestOptions);
  };

  const placeOrderRetry = ({ orderId }: { orderId: number; storeId: string }) => {
    const requestOptions: AxiosRequestConfig | any = {
      method: METHOD.POST,
      url: `/orders/${orderId}/place-retry`,
    };

    return executeRequest(requestOptions);
  };

  return {
    findOrders,
    lockOrderREST,
    unlockOrderREST,
    findOrderByID,
    cancelOrder,
    postOrderCommentByID,
    getOrderCommentByID,
    getOrdersPagination,
    getOrdersPaginationBySeller,
    getOrdersById,
    processOrderItem,
    updateOrderItemFfmCenter,
    cancelOrderItem,
    placeOrderRetry,
    reOrder,
  };
};
